import React from 'react';
import _ from 'lodash';
// eslint-disable-next-line
import FuzzySearch from 'fuzzy-search';
import {
  getDynamicCorpData,
  toolSearchMetaEntries,
  BrowserPaths,
  BrowserPathSearchName,
} from '@scale/shared/corpSearch';
import {
  SearchMetaEntry,
  SearchResultActionType,
  SearchResultType,
} from '@scale/shared/corpSearch/types';
import { getHistory, HistoryEntry } from 'react-scale/corp/lib/localHistoryStore';
import { getNameFromPath } from '@scale/shared/corpSearch/utils';
import { matchesLookupId } from 'react-scale/corp/lib/parseInputText';
import { useAllDynamicCorpPageTitles } from 'react-scale/corp/hooks/useAllDynamicCorpPageTitles';

const DEFAULT_NAME = 'Scale - Corporate';
const IGNORE_PATHS = ['lookup', 'search'];
const MAX_RESULTS = 15;

const corpSearchEntry: SearchMetaEntry = {
  type: SearchResultType.CorpSearch,
  name: 'Search Corp',
  action: {
    type: SearchResultActionType.CorpSearch,
  },
};

const corpLookupEntry: SearchMetaEntry = {
  type: SearchResultType.CorpSearch,
  name: 'Lookup ID',
  action: {
    type: SearchResultActionType.CorpLookup,
  },
};

const getHistoryName = (entry: HistoryEntry) => {
  if (entry.value === DEFAULT_NAME) {
    // Generate name from path
    return getNameFromPath(entry.id);
  } else {
    return entry.value;
  }
};

const searchTypeForHistoryType: Record<string, SearchResultType> = {
  project: SearchResultType.Project,
  tasker: SearchResultType.Tasker,
  customer: SearchResultType.Customer,
  course: SearchResultType.Course,
  workerteam: SearchResultType.Team,
  cdsurl: SearchResultType.CDSUrl,
};

const isToolEntry = (entry: HistoryEntry | SearchMetaEntry): entry is SearchMetaEntry =>
  !!entry.action;

const historyEntryToSearchResultIfNeeded = (
  entry: HistoryEntry | SearchMetaEntry,
): SearchMetaEntry => {
  if (isToolEntry(entry)) {
    return entry;
  }
  return {
    type: searchTypeForHistoryType[entry.type] ?? SearchResultType.Tool,
    name: getHistoryName(entry),
    description: entry.description,
    action:
      entry.type === 'tool'
        ? {
            type: SearchResultActionType.Link,
            path: entry.id,
            query: entry.query,
          }
        : entry.type === 'project'
        ? {
            type: SearchResultActionType.Link,
            path: 'qm/queue/' + entry.id,
          }
        : {
            type: SearchResultActionType.CorpLookup,
            id: entry.id,
          },
    id: entry.id,
  };
};

const getUniqueKey = (result: SearchMetaEntry): string => JSON.stringify(result.action);

const isValidResult = (result: SearchMetaEntry | HistoryEntry) =>
  isToolEntry(result) || !matchesLookupId(result.value) || result.type === 'cdsurl';

const projectEntriesCanonicalForm = (result: SearchMetaEntry) => {
  const { id } = result;
  if (!id) return result;
  const deliveryPortalRegex = /(qm\/queue\/([0-9a-z]{24})).*/;
  const match = id.match(deliveryPortalRegex);
  const DELIVERY_PORTAL_TOKEN = ' [Delivery Portal]';
  if (match) {
    // Condense a delivery portal entry
    return {
      ...result,
      id: match[2],
      type: SearchResultType.Project,
      name: result.name + DELIVERY_PORTAL_TOKEN,
    };
  } else if (result.type === SearchResultType.Project) {
    // Convert a project entry to delivery portal
    return {
      ...result,
      name: result.name + DELIVERY_PORTAL_TOKEN,
    };
  }
  return result;
};

const condenseProjectEntries = (result: SearchMetaEntry) => {
  if (result.type === SearchResultType.Project) return result.id; // Projects are condensed by ID
  return result; // All other entries are considered unique
};

const useCorpSearch = (searchText: string, isRemoAdmin?: boolean) => {
  const dynamicCorpPageTitles = useAllDynamicCorpPageTitles();
  const recentHistory = React.useMemo<HistoryEntry[]>(
    () =>
      _.sortBy(getHistory() || [], item => -item.lastSearched)
        .filter(entry => !_.some(IGNORE_PATHS.map(path => entry.id.startsWith(path))))
        .slice(0, MAX_RESULTS),
    [],
  );

  const searchIndex = React.useMemo(
    () =>
      new FuzzySearch<SearchMetaEntry | HistoryEntry>(
        isRemoAdmin
          ? [...(getHistory() || [])]
          : [
              ...toolSearchMetaEntries,
              ...getDynamicCorpData(dynamicCorpPageTitles),
              ...(getHistory() || []),
              ...getAllSearchableCorpBrowserPaths(),
            ],
        ['name', 'keywords', 'value'],
        {
          sort: true,
        },
      ),
    [dynamicCorpPageTitles, isRemoAdmin],
  );

  const [results, setResults] = React.useState<SearchMetaEntry[]>([]);

  React.useEffect(() => {
    if (!searchText) {
      setResults(
        _.uniqBy(
          recentHistory
            .filter(isValidResult)
            .map(historyEntryToSearchResultIfNeeded)
            .map(projectEntriesCanonicalForm),
          condenseProjectEntries,
        ).slice(0, MAX_RESULTS),
      );
      return;
    }

    const searchResults = searchIndex
      .search(searchText)
      // HACK: filter out all entries with an ID in the name since this comes from an invalid state
      .filter(isValidResult)
      .slice() as (SearchMetaEntry | HistoryEntry)[];
    const nextResults = _.uniqBy(
      [
        // Show search or lookup depending on input
        matchesLookupId(searchText) ? corpLookupEntry : corpSearchEntry,
        // Show matching recents & tools
        ...searchResults.map(historyEntryToSearchResultIfNeeded),
      ],
      getUniqueKey,
    );

    const resultsCollapsingDeliveryPortal = _.uniqBy(
      nextResults.map(projectEntriesCanonicalForm),
      condenseProjectEntries,
    ).slice(0, MAX_RESULTS);

    setResults(resultsCollapsingDeliveryPortal);
  }, [searchText, searchIndex, recentHistory]);

  return { results };
};

const getAllSearchableCorpBrowserPaths = () => {
  const paths = Object.values(BrowserPaths).filter(path => {
    if (path.includes(':')) {
      return false;
    }
    return true;
  });
  const titles: Record<string, number> = {};
  const parsedPaths = paths.map((path: string) => {
    let pageTitle = _.get(BrowserPathSearchName, [path]) || generateReasonableName(path);
    if (titles[pageTitle]) {
      const count = titles[pageTitle];
      titles[pageTitle] = count + 1;
      pageTitle = `${pageTitle} (${count})`;
    } else {
      titles[pageTitle] = 1;
    }
    return {
      name: pageTitle,
      type: SearchResultType.Page,
      action: {
        type: SearchResultActionType.Link,
        path: path.substring(1),
      },
    };
  });

  return parsedPaths;
};

function generateReasonableName(path: string): string {
  // Remove leading and trailing slashes
  path = path.replace(/^\/|\/$/g, '');

  // Split the path into segments using '/'
  const segments = path.split('/');

  // Process each segment
  const cleanedSegments = segments.map(segment => {
    // Remove hyphens and underscores
    segment = segment.replace(/[-_]/g, ' ');

    // Capitalize the first letter
    segment = segment.charAt(0).toUpperCase() + segment.slice(1);

    return segment;
  });

  // Join the cleaned segments into a single string
  const reasonableName = cleanedSegments.join(' ');

  return reasonableName;
}

export default useCorpSearch;
